import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Lock, User, AlertCircle, ShieldCheck, KeyRound } from "lucide-react";
import api from "@/lib/api";

const Brand = ({ size = "text-3xl sm:text-4xl" }) => (
  <>
    <h2 className={`${size} font-black tracking-tight text-foreground`}>
      Crawler<span className="inline-block w-[0.22em] h-[0.22em] rounded-full bg-[#DBE6EC] ml-[0.08em] align-baseline" style={{ marginBottom: '0.18em' }}></span>
    </h2>
    <p className="text-sm text-muted-foreground mt-1">
      <span className="font-light">by</span> <span className="font-bold tracking-wide">OFFGROUND</span>
    </p>
  </>
);

export const AuthShell = ({ children }) => (
  <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
    <div className="hidden lg:flex lg:w-1/2 relative items-end p-12 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black">
      <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, hsl(var(--primary) / 0.35), transparent 55%)" }} />
      <div className="relative z-10 space-y-2">
        <h1 className="text-5xl font-black tracking-tight text-white">
          Crawler<span className="inline-block w-[0.22em] h-[0.22em] rounded-full bg-[#DBE6EC] ml-[0.08em] align-baseline" style={{ marginBottom: '0.18em' }}></span>
        </h1>
        <p className="text-xl text-white/60">
          <span className="font-light">by</span> <span className="font-bold tracking-wide">OFFGROUND</span>
        </p>
        <p className="text-sm text-white/40 pt-2">CMS-Erkennung. Drupal-Versionen. Lead Intelligence.</p>
      </div>
    </div>
    <div className="flex-1 flex items-center justify-center p-6 sm:p-8 bg-background">
      <div className="w-full max-w-sm">
        <div className="mb-8 sm:mb-10"><Brand /></div>
        {children}
      </div>
    </div>
  </div>
);

export const ErrorBox = ({ children }) => (
  <div data-testid="login-error" className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-[var(--radius)] px-3 py-2">
    <AlertCircle className="w-4 h-4 shrink-0" />
    {children}
  </div>
);

const inputCls = "pl-10 bg-card border-border text-foreground placeholder:text-muted-foreground h-11 rounded-[var(--radius)]";
const buttonCls = "w-full h-11 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary)/0.85)] text-[hsl(var(--primary-foreground))] font-semibold rounded-[var(--radius)]";

export default function LoginPage({ onLogin }) {
  const [step, setStep]         = useState("password");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode]         = useState("");
  const [setup, setSetup]       = useState(null);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { username, password });
      setPassword("");
      if (res.data.step === "mfa_setup") {
        setSetup(res.data);
        setStep("mfa_setup");
      } else {
        setStep("mfa");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/mfa", { code });
      onLogin(res.data.user);
    } catch (err) {
      const detail = err.response?.data?.detail || "Code ungültig";
      setError(detail);
      setCode("");
      if (err.response?.status === 401 && detail.includes("abgelaufen")) {
        setStep("password");
        setSetup(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (step === "password") {
    return (
      <AuthShell>
        <form onSubmit={submitPassword} className="space-y-4 sm:space-y-5">
          <div className="space-y-1.5">
            <label className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Benutzername</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="login-username-input" value={username} autoComplete="username"
                     onChange={(e) => setUsername(e.target.value)} placeholder="Benutzername" className={inputCls} autoFocus />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">Passwort</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="login-password-input" type="password" value={password} autoComplete="current-password"
                     onChange={(e) => setPassword(e.target.value)} placeholder="Passwort" className={inputCls} />
            </div>
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <Button data-testid="login-submit-btn" type="submit" disabled={loading || !username || !password} className={buttonCls}>
            {loading ? "Anmelden…" : "Weiter"}
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <form onSubmit={submitCode} className="space-y-4 sm:space-y-5">
        {step === "mfa_setup" && setup && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" /> Zwei-Faktor-Anmeldung einrichten
            </div>
            <p className="text-xs text-muted-foreground">
              Scanne den QR-Code mit einer Authenticator-App (z. B. 1Password, Microsoft oder Google Authenticator)
              und gib den 6-stelligen Code ein.
            </p>
            <div className="bg-white rounded-[var(--radius)] p-3 w-fit mx-auto">
              <img src={setup.qr_svg} alt="QR-Code für die Authenticator-App" className="w-44 h-44" />
            </div>
            <p className="text-[11px] text-muted-foreground break-all">
              Manueller Schlüssel: <span className="font-mono text-foreground">{setup.secret}</span>
            </p>
          </div>
        )}
        {step === "mfa" && (
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <KeyRound className="w-4 h-4 text-[hsl(var(--primary))]" /> Code aus der Authenticator-App
          </div>
        )}
        <Input data-testid="login-mfa-input" value={code} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
               onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" autoFocus
               className="bg-card border-border text-foreground h-11 text-center tracking-[0.5em] font-mono text-lg rounded-[var(--radius)]" />
        {error && <ErrorBox>{error}</ErrorBox>}
        <Button data-testid="login-mfa-btn" type="submit" disabled={loading || code.length !== 6} className={buttonCls}>
          {loading ? "Prüfe…" : "Anmelden"}
        </Button>
        <button type="button" onClick={() => { setStep("password"); setSetup(null); setCode(""); setError(""); }}
                className="w-full text-xs text-muted-foreground hover:text-foreground">
          Zurück
        </button>
      </form>
    </AuthShell>
  );
}
